import React, { useState, useEffect } from "react";
import {
  FaStore,
  FaFilter,
  FaSearch,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaClock,
  FaUserCheck,
  FaTimes,
  FaArchive,
  FaChartBar,
  FaSpinner,
  FaPhone,
  FaBuilding,
} from "react-icons/fa";
import { vendorService } from "./vendor.service";
import { VendorQueryParams } from "./vendor.types";

/* ================= STAT CARD ================= */
const StatCard = ({ icon, title, value, color }) => (
  <div className={`relative p-6 text-white transition-all duration-300 rounded-3xl shadow-xl hover:shadow-2xl hover:-translate-y-1 ${color}`}>
    <div className="absolute inset-0 rounded-3xl bg-white/10 backdrop-blur-lg"></div>
    <div className="relative flex items-center gap-5">
      <div className="flex items-center justify-center w-16 h-16 text-2xl rounded-2xl bg-white/20 backdrop-blur-md">
        {icon}
      </div>
      <div>
        <p className="text-sm tracking-widest uppercase opacity-90">{title}</p>
        <h3 className="text-4xl font-bold">{value}</h3>
      </div>
    </div>
  </div>
);

/* ================= STATUS BADGE ================= */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: "bg-yellow-100 text-yellow-700", label: "Pending", icon: <FaClock /> },
    contacted: { color: "bg-blue-100 text-blue-700", label: "Contacted", icon: <FaPhone /> },
    approved: { color: "bg-green-100 text-green-700", label: "Approved", icon: <FaCheckCircle /> },
    rejected: { color: "bg-red-100 text-red-700", label: "Rejected", icon: <FaTimes /> },
    archived: { color: "bg-purple-100 text-purple-700", label: "Archived", icon: <FaArchive /> },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full ${config.color}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

/* ================= VENDOR DETAIL MODAL ================= */
const VendorDetailModal = ({ vendor, onClose, onStatusUpdate }) => {
  if (!vendor) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="relative w-full max-w-2xl overflow-hidden bg-white shadow-2xl rounded-3xl">
        {/* Header */}
        <div className="p-6 text-white bg-gradient-to-r from-[#6a0dad] to-[#9f5bd8]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20">
                <FaStore className="text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Vendor Details</h2>
                <p className="text-sm opacity-90">Registration Information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 transition rounded-lg hover:bg-white/20"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-600">Name</label>
              <p className="text-lg font-medium text-gray-900">{vendor.name}</p>
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-600">Phone Number</label>
              <p className="text-lg font-medium text-gray-900">{vendor.phone_number}</p>
            </div>
            <div className="col-span-2">
              <label className="block mb-2 text-sm font-semibold text-gray-600">Company/Store Name</label>
              <p className="text-lg font-medium text-gray-900">{vendor.company_store_name}</p>
            </div>
            <div className="col-span-2">
              <label className="block mb-2 text-sm font-semibold text-gray-600">Message</label>
              <p className="p-4 text-gray-700 bg-gray-50 rounded-xl">{vendor.message || "No message provided"}</p>
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-600">Status</label>
              <StatusBadge status={vendor.status} />
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-600">Registration Date</label>
              <p className="text-sm text-gray-700">
                {new Date(vendor.created_at).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-6 border-t">
            <select
              className="flex-1 px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#6a0dad]"
              value={vendor.status}
              onChange={(e) => onStatusUpdate(vendor.id, e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={onClose}
              className="px-6 py-3 text-white transition bg-gradient-to-r from-[#6a0dad] to-[#9f5bd8] rounded-xl hover:scale-105"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================= MAIN COMPONENT ================= */
export default function VendorRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    fetchRegistrations();
    fetchStats();
  }, [filters]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const params = new VendorQueryParams(
        filters.status || null,
        filters.search,
        filters.page,
        filters.limit
      );
      const response = await vendorService.getRegistrations(params);
      if (response.status) {
        setRegistrations(response.registrations);
      }
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await vendorService.getStats();
      if (response.status) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await vendorService.updateStatus(id, { status: newStatus });
      fetchRegistrations();
      fetchStats();
      setSelectedVendor(null);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this registration?")) {
      try {
        await vendorService.deleteRegistration(id);
        fetchRegistrations();
        fetchStats();
      } catch (error) {
        console.error("Failed to delete registration:", error);
      }
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await vendorService.getRegistration(id);
      if (response.status) {
        setSelectedVendor(response.registration);
      }
    } catch (error) {
      console.error("Failed to fetch vendor details:", error);
    }
  };

  return (
    <div className="min-h-screen p-4">
      {/* HERO HEADER */}
      <div className="flex items-center justify-between mb-12">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-14 h-14 text-white rounded-2xl shadow-md bg-gradient-to-br from-[#ffc933] to-[#fde193]">
            <FaStore className="text-xl" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Vendor Registrations</h1>
            <p className="text-sm text-gray-500">Manage vendor applications and partnerships</p>
          </div>
        </div>
      </div>

      {/* STATS GRID */}
      {stats && (
        <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<FaStore />}
            title="Total Vendors"
            value={stats.total || 0}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
          />
          <StatCard
            icon={<FaClock />}
            title="Pending"
            value={stats.pending || 0}
            color="bg-gradient-to-br from-yellow-500 to-yellow-600"
          />
          <StatCard
            icon={<FaUserCheck />}
            title="Approved"
            value={stats.approved || 0}
            color="bg-gradient-to-br from-green-500 to-green-600"
          />
          <StatCard
            icon={<FaChartBar />}
            title="Last 30 Days"
            value={stats.last_30_days || 0}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
          />
        </div>
      )}

      {/* FILTERS & ACTIONS */}
      <div className="p-6 mb-8 bg-white shadow-xl rounded-3xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Search */}
          <div className="relative flex-1">
            <FaSearch className="absolute text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
            <input
              type="text"
              placeholder="Search by name, phone, or company..."
              className="w-full py-3 pl-12 pr-4 border rounded-xl focus:ring-2 focus:ring-[#ffc933] focus:outline-none"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FaFilter className="absolute text-gray-400 transform -translate-y-1/2 left-4 top-1/2" />
            <select
              className="py-3 pl-12 pr-8 border rounded-xl focus:ring-2 focus:ring-[#ffc933] focus:outline-none appearance-none"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* REGISTRATIONS TABLE */}
      <div className="overflow-hidden bg-white shadow-xl rounded-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FaSpinner className="text-4xl animate-spin text-[#ffc933]" />
          </div>
        ) : registrations.length === 0 ? (
          <div className="py-20 text-center text-gray-500">
            <FaStore className="mx-auto mb-4 text-6xl opacity-20" />
            <p className="text-lg">No vendor registrations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#ffc933] to-[#fde193] text-gray-900">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Phone</th>
                  <th className="p-4 text-left">Company/Store</th>
                  <th className="p-4 text-left">Status</th>
                  <th className="p-4 text-left">Date</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((vendor, idx) => (
                  <tr
                    key={vendor.id}
                    className={`border-b transition hover:bg-gray-50 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <td className="p-4 font-medium text-gray-900">{vendor.name}</td>
                    <td className="p-4 text-gray-600">{vendor.phone_number}</td>
                    <td className="p-4 text-gray-600">
                      <div className="flex items-center gap-2">
                        <FaBuilding className="text-gray-400" />
                        {vendor.company_store_name}
                      </div>
                    </td>
                    <td className="p-4">
                      <StatusBadge status={vendor.status} />
                    </td>
                    <td className="p-4 text-sm text-gray-500">
                      {new Date(vendor.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewDetails(vendor.id)}
                          className="p-2 text-blue-500 transition rounded-lg hover:bg-blue-50"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <select
                          className="px-3 py-2 text-xs border rounded-lg focus:ring-2 focus:ring-[#ffc933]"
                          value={vendor.status}
                          onChange={(e) => handleStatusChange(vendor.id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                          <option value="archived">Archived</option>
                        </select>
                        <button
                          onClick={() => handleDelete(vendor.id)}
                          className="p-2 text-red-500 transition rounded-lg hover:bg-red-50"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* VENDOR DETAIL MODAL */}
      {selectedVendor && (
        <VendorDetailModal
          vendor={selectedVendor}
          onClose={() => setSelectedVendor(null)}
          onStatusUpdate={handleStatusChange}
        />
      )}
    </div>
  );
}